import { createDelayedQueue } from '@ux/queue';
import { useCallback, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';
import { isBrowser } from '@ux/js';

// checks whether GA is on the page and the tealium_0 agent is loaded
const createTest$2 = window => () => {
  const {
    ga
  } = window;
  if (typeof ga !== 'function') {
    return false;
  }
  if (typeof ga.getAll !== 'function') {
    return false;
  }
  const names = ga.getAll().map(x => x.get('name'));
  return names.some(name => name === 'tealium_0');
};

// This is the most basic way to send something through GA
const dispatch$1 = (window, createQueue) => {
  const test = createTest$2(window);
  const action = function (evt) {
    const {
      ga
    } = window;
    for (var _len = arguments.length, data = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      data[_key - 1] = arguments[_key];
    }
    ga(`tealium_0.${evt}`, ...data);
  };
  return createQueue(action, test);
};

// Sends analytics directly through GA
const dispatchGAEvent$1 = dispatch => {
  if (dispatch == null) {
    return () => {};
  }
  return dispatch.bind(null, 'send');
};

// Sends ecommerce data directly through GA
// note that you also need to dispatch a normal GA event in order to send
// the ecommerce data
const dispatchEcommEvent$1 = dispatch => function (evt) {
  if (dispatch == null) {
    return;
  }
  for (var _len2 = arguments.length, data = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    data[_key2 - 1] = arguments[_key2];
  }
  return dispatch(`ec:${evt}`, ...data);
};

// This method sends ecommerce data, but automatically dispatches the GA event which
// carries the ecommerce data with it
const ecommerce$1 = (dispatchEcommEvent, dispatchGAEvent) => function (events) {
  let gaParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (dispatchEcommEvent != null) {
    events.forEach(event => dispatchEcommEvent(...event));
  }
  if (dispatchGAEvent != null) {
    dispatchGAEvent({
      hitType: 'event',
      eventCategory: 'Ecommerce',
      eventAction: 'send',
      ...gaParams
    });
  }
};

// checks whether Facebook pixel is on the page
const createTest$1 = window => () => {
  var _fbq$instance$pixelsB, _fbq$instance;
  const {
    fbq
  } = window;
  if (typeof fbq !== 'function') {
    return false;
  }
  if (Object.keys((_fbq$instance$pixelsB = (_fbq$instance = fbq.instance) === null || _fbq$instance === void 0 ? void 0 : _fbq$instance.pixelsByID) != null ? _fbq$instance$pixelsB : {}).length === 0) {
    return false;
  }
  return fbq.loaded;
};

// This is the most basic way to send something through FB
const dispatchFB$1 = (window, createQueue) => {
  const test = createTest$1(window);
  const action = (evt, data) => {
    const {
      fbq
    } = window;
    fbq('track', evt, data);
  };
  return createQueue(action, test);
};
const dispatchFBEvent$1 = dispatch => (evt, data) => {
  if (dispatch == null) {
    return;
  }
  return dispatch(evt, data);
};

// @ts-ignore: utag should be defined
const getUtag = window => window.utag;
const setUtagData = (window, data) => {
  // eslint-disable-next-line camelcase
  window.utag_data = data;
};

// checks whether GA is on the page and the tealium_0 agent is loaded
const createTest = window => () => {
  const utag = getUtag(window);
  return utag != null && typeof utag.link === 'function' && typeof utag.view === 'function';
};

// Sends analytics via tealium
const link$1 = (window, createQueue) => {
  const test = createTest(window);
  const action = data => {
    setUtagData(window, data);
    const utag = getUtag(window);
    utag.link(data);
  };
  return createQueue(action, test);
};

// Send tealium a page view request
const view$1 = (window, createQueue) => {
  const test = createTest(window);
  const action = data => {
    setUtagData(window, data);
    const utag = getUtag(window);
    utag.view(data);
  };
  return createQueue(action, test);
};

const link = link$1(window, createDelayedQueue);
const view = view$1(window, createDelayedQueue);
const dispatch = dispatch$1(window, createDelayedQueue);
const dispatchFB = dispatchFB$1(window, createDelayedQueue);
const dispatchGAEvent = dispatchGAEvent$1(dispatch);
const dispatchFBEvent = dispatchFBEvent$1(dispatchFB);
const dispatchEcommEvent = dispatchEcommEvent$1(dispatch);
const ecommerce = ecommerce$1(dispatchEcommEvent, dispatchGAEvent);

function trackInteractionHOC(options) {
  const config = {
    event: 'interaction',
    trigger: 'onClick',
    ...options
  };
  return function interaction(Component) {
    return function WithInteraction(props) {
      const original = props[config.trigger];
      const handler = function () {
        if (link != null) {
          link({
            event: config.event,
            source: config.source
          });
        }
        if (typeof original === 'function') {
          original(...arguments);
        }
      };
      const injectProps = {
        [config.trigger]: handler
      };
      return /*#__PURE__*/jsx(Component, {
        ...props,
        ...injectProps
      });
    };
  };
}

/**
 * @deprecated This will be removed in the next major version. Just import { dispatchGAEvent } from '@ux/tagmg' instead.
 */
const useGAEvent = () => {
  return useCallback(args => {
    if (dispatchGAEvent != null) {
      dispatchGAEvent(args);
    }
  }, []);
};
var useGAEvent$1 = useGAEvent;

const usePageView = function () {
  var _window$location;
  let {
    pageviewTest,
    dispatch = dispatchGAEvent
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const pathname = isBrowser() ? (_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.pathname : '';
  useEffect(() => {
    const shouldDispatch = pageviewTest == null ? true : pageviewTest(window.location);
    if (dispatch != null && shouldDispatch) {
      dispatch({
        hitType: 'pageview',
        page: pathname
      });
    }
  }, [pathname, dispatch, pageviewTest]);
};
function useUsePageView() {
  let {
    pageviewTest
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return usePageView({
    pageviewTest
  });
}

export { dispatch, dispatchEcommEvent, dispatchFB, dispatchFBEvent, dispatchGAEvent, ecommerce, link, trackInteractionHOC as trackInteraction, useGAEvent$1 as useGAEvent, useUsePageView as usePageView, view };
